import { Popover, Tooltip } from '@material-ui/core';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankOutlinedIcon from '@material-ui/icons/CheckBoxOutlineBlankOutlined';
import CSS from 'csstype';
import { cloneDeep } from 'lodash-es';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { FixedSizeList } from 'react-window';

import { ISelectOption, ISelectPreset } from '~/src/dash/components/dropdowns/common';
import { ActiveQuickFilter } from '~/src/dash/components/dropdowns/SelectPill/ActiveQuickFilter';
import { EActiveFilter, statuses } from '~/src/dash/components/dropdowns/SelectPill/common';
import { CaretMiniDownIcon } from '~/src/dash/components/icons/misc-icons';
import {
  borderRadius,
  Col,
  Cols,
  Row,
  Rows,
  spacing,
  Text,
  TextButton,
  TextInput,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { FONT_WEIGHT, fontSizing } from '~/src/dash/components/text/common';

const getSingleLabel = (
  selectedID: number | string,
  options: ISelectOption[],
  singularLabel: string,
) => {
  const selectedOption = options.find((option) => option.id === selectedID);
  return selectedOption?.label || `1 ${singularLabel} Selected`;
};

const getHeight = (numItems: number) => {
  if (numItems >= 12) return 360;
  return numItems * 30 + 8;
};

const sortList = (
  list: ISelectOption[],
  selectedIDs: Set<number | string>,
  isSortDisabled: boolean,
  extractStringToSort?: (option: ISelectOption) => string,
) => {
  if (isSortDisabled) return [...list];
  const sorted = list.sort((a, b) => {
    if (
      (selectedIDs.has(a.id) && selectedIDs.has(b.id)) ||
      (!selectedIDs.has(a.id) && !selectedIDs.has(b.id))
    ) {
      if (extractStringToSort) {
        return extractStringToSort(a).localeCompare(extractStringToSort(b));
      } else {
        return a.label.localeCompare(b.label);
      }
    } else if (
      (!selectedIDs.has(a.id) && selectedIDs.has(b.id)) ||
      (selectedIDs.has(a.id) && !selectedIDs.has(b.id))
    ) {
      return selectedIDs.has(a.id) > selectedIDs.has(b.id) ? -1 : 1;
    } else {
      return 0;
    }
  });
  return [...sorted];
};

const queryList = (query: string, list: ISelectOption[]) => {
  let queriedList = cloneDeep(list);
  if (query) {
    const casedQuery = query.toLowerCase();
    queriedList = list.filter(
      (item) => String(item.id).includes(query) || item.label.toLowerCase().includes(casedQuery),
    );
  }
  return queriedList;
};

const extractActiveFilteredList = (list: ISelectOption[], activeStatus: EActiveFilter) => {
  switch (activeStatus) {
    case EActiveFilter.SHOW_ALL:
      return list;
    case EActiveFilter.ACTIVE:
      return list.filter((item) => item.isActive);
    case EActiveFilter.INACTIVE:
      return list.filter((item) => !item.isActive);
  }
};

const defaultStyle: CSS.Properties = {
  background: publicaColors.veryLightGray,
  transition: 'all 0.5s ease-in-out',
};

const getPillStyle = (
  isDisabled: boolean,
  isActive: boolean,
  isHovered: boolean = false,
): CSS.Properties => {
  const bgColor = isActive ? publicaColors.honeydew : publicaColors.veryLightGray;
  return {
    margin: '2px 8px 2px 0px',
    fontWeight: FONT_WEIGHT.bold,
    color: isActive ? publicaColors.jungleGreen : publicaColors.veryDarkGray,
    backgroundColor: !isDisabled && isHovered && !isActive ? publicaColors.lightGray : bgColor,
  };
};

const getStyle = (
  theme: string,
  isActive: boolean,
  isHovered: boolean,
  isDisabled: boolean,
): CSS.Properties => {
  switch (theme) {
    case 'pill':
      return getPillStyle(isDisabled, isActive, isHovered);
    default:
      return defaultStyle;
  }
};

const pillTextStyle: CSS.Properties = {
  fontWeight: 500,
};

const getTextStyle = (theme: string): CSS.Properties => {
  let style: CSS.Properties = {};
  switch (theme) {
    case 'pill':
      style = pillTextStyle;
  }

  return style;
};

const generateLabel = (
  listLabel: string,
  singularLabel: string,
  placeholder: string,
  isSingleSelect: boolean,
  numSelected: number,
  totalOptions: number,
  selectedLabel: string,
  theme: IndependentSelectTheme,
): string => {
  switch (true) {
    // pill labels
    case isSingleSelect && numSelected === 1 && theme === 'pill':
      return `${listLabel} ${selectedLabel}`;
    case numSelected === 1 && theme === 'pill':
      return `${numSelected} ${singularLabel}  Selected`;
    case numSelected > 0 && theme === 'pill':
      return `${numSelected} ${listLabel} Selected`;
    case theme === 'pill':
      return listLabel;
    // default labels
    case totalOptions === 0:
      return `No ${listLabel} Found`;
    case isSingleSelect && numSelected === 1:
    case numSelected === 1:
      return selectedLabel || `${numSelected} ${singularLabel}  Selected`;
    case numSelected === totalOptions:
      return `All ${listLabel} Selected`;
    case numSelected > 1:
      return `${numSelected} ${listLabel} Selected`;

    default:
      return placeholder;
  }
};

const checkboxStyle: CSS.Properties = {
  marginRight: spacing.small,
  color: publicaColors.darkGray,
};

interface IVirtualSelectRowProps {
  handleOnChange: (option: ISelectOption) => void;
  index: number;
  options: ISelectOption[];
  selectedIDs: Set<string | number>;
  isCheckboxHidden: boolean;
  style: React.CSSProperties;
  displayID?: boolean;
  enableLabelTooltip?: boolean;
}

const VirtualSelectRow = ({
  index,
  style,
  options,
  handleOnChange,
  displayID = false,
  enableLabelTooltip,
  selectedIDs,
  isCheckboxHidden,
}: IVirtualSelectRowProps) => {
  const { id, label } = options[index];
  const isSelected = selectedIDs.has(id);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Row
      onClick={() => handleOnChange(options[index])}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={
        {
          ...style,
          cursor: 'pointer',
          padding: '4px 8px 0px',
          backgroundColor: isHovered ? publicaColors.veryLightGray : 'inherit',
        } as CSS.Properties
      }
      className={`virtual-row-${index}`}
    >
      <Cols gap={spacing.small} style={{ alignItems: 'flex-start' }}>
        {!isCheckboxHidden && (
          <Col>
            {isSelected ? (
              <CheckBoxIcon style={checkboxStyle} />
            ) : (
              <CheckBoxOutlineBlankOutlinedIcon style={checkboxStyle} />
            )}
          </Col>
        )}
        {displayID && (
          <Col style={{ minWidth: '40px', paddingTop: '1px' }}>
            <Text small muted bold={isSelected}>
              {id}
            </Text>
          </Col>
        )}
        <Col style={{ paddingTop: '1px' }}>
          <Tooltip title={enableLabelTooltip ? label : ''}>
            <div>
              <Text
                small
                bold={isSelected}
                style={{
                  width: '320px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {label}
              </Text>
            </div>
          </Tooltip>
        </Col>
      </Cols>
    </Row>
  );
};

type IndependentSelectTheme = 'default' | 'pill';

export interface IIndependentSelectProps {
  placeholder: string;
  listLabel: string;
  options: ISelectOption[];
  selectedOptionIDs: Set<number | string>;
  displayID?: boolean;
  selectedLimit?: number;
  isSelectionRequired?: boolean;
  isCheckboxHidden?: boolean;
  enableLabelTooltip?: boolean;
  enableActiveFilter?: boolean;
  singularLabel: string;
  handleApplySelect: (value: Set<number | string>, key?: string | number) => void;
  disableSearch?: boolean;
  disableSort?: boolean;
  isDisabled?: boolean;
  height?: number | string;
  maxWidth?: number;
  minWidth?: number;
  popoverWidth?: number;
  onLoadDisplayActiveOnly?: boolean;
  extractStringToSort?: (option: ISelectOption) => string;
  presets?: ISelectPreset[];
  key: string | number;
  selectedKey?: string | number;
  theme?: IndependentSelectTheme;
  iconName?: string; // only can be used with pill theme
}

// USE ONLY IF YOU WANT THE DROPDOWN TO BE SELF-CONTAINED
// ONCE INITIALIZED, THE PARENT CANNOT CHANGE THE OPTIONS OR SELECTED
// USE handleApplySelect TO UPDATE PARENT
export const IndependentSelect = ({
  placeholder,
  listLabel,
  options,
  isSelectionRequired,
  selectedOptionIDs = new Set([]),
  displayID = false,
  selectedLimit,
  isCheckboxHidden = false,
  enableLabelTooltip,
  enableActiveFilter,
  singularLabel,
  handleApplySelect,
  disableSearch = false,
  disableSort = false,
  isDisabled = false,
  height = 32,
  maxWidth = 480,
  minWidth = 240,
  popoverWidth,
  onLoadDisplayActiveOnly = false,
  presets,
  extractStringToSort,
  selectedKey,
  theme = 'default',
  iconName,
}: IIndependentSelectProps): JSX.Element => {
  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [sortedOptions, setSortedOptions] = useState<ISelectOption[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<ISelectOption[]>([]);
  const [search, setSearch] = useState('');
  const [selectedActiveStatus, setSelectedActiveStatus] = useState(
    onLoadDisplayActiveOnly ? EActiveFilter.ACTIVE : EActiveFilter.SHOW_ALL,
  );

  const isSingleSelect = selectedLimit === 1;

  const totalCount = options.length || 0;
  const [hasSaved, setHasSaved] = useState(selectedOptionIDs.size > 0);

  const [selectedIDs, setSelectedIDs] = useState<Set<number | string>>(selectedOptionIDs);
  const [selectedLabel, setSelectedLabel] = useState(
    selectedOptionIDs.size === 1
      ? getSingleLabel(selectedOptionIDs.values().next().value, options, singularLabel)
      : '',
  );

  const [errorMessage, setErrorMessage] = useState('');

  const isMounted = useRef(false);
  const hasChangeOccurred = useRef(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const isSingleSelectionRequired = selectedLimit === 1 && isSelectionRequired;

  const handleApply = (selectedKey?: string | number): void => {
    handleApplySelect(selectedIDs, selectedKey);
    setHasSaved(selectedIDs.size > 0);
  };

  useEffect(() => {
    const sorted = sortList(options, selectedIDs, disableSort, extractStringToSort);
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSearch('');
    });
  }, [options]);

  // filter options upon search
  useEffect(() => {
    if (isMounted.current) {
      const queriedList = queryList(search, sortedOptions);
      const activeFilteredList = extractActiveFilteredList(queriedList, selectedActiveStatus);
      setFilteredOptions(activeFilteredList);
    }
  }, [search, sortedOptions, selectedActiveStatus]);

  // if the dropdown closes immediately after a selection, handleApplySelect here instead
  useEffect(() => {
    if (isMounted.current && isSingleSelectionRequired) {
      handleApply(selectedKey);
    }
    isMounted.current = true;
  }, [selectedIDs]);

  const handleOnOpen = (e: React.MouseEvent) => {
    if (!isDisabled) setAnchorEl(e.currentTarget);
  };

  const handleOnClose = (forceClose?: boolean): void => {
    if (!forceClose && selectedIDs.size === 0) {
      setErrorMessage('You must select an option to continue.');
      return;
    }
    unstable_batchedUpdates(() => {
      if (errorMessage) setErrorMessage('');
      setSearch('');
      setAnchorEl(null);
    });
    if (!isSingleSelectionRequired) handleApply(selectedKey);
  };

  const handleOnChange = (option: ISelectOption) => {
    const { id, label } = option;
    const clonedSelectedIDs = cloneDeep(selectedIDs);
    let updatedLabel = '';
    const selectedCount = clonedSelectedIDs.size;

    if (isSingleSelect) {
      if (clonedSelectedIDs.has(id)) {
        if (isSelectionRequired && selectedCount === 1) return;
        clonedSelectedIDs.delete(id);
      } else if (selectedCount === 1) {
        clonedSelectedIDs.clear();
        clonedSelectedIDs.add(id);
        updatedLabel = label;
      } else {
        clonedSelectedIDs.add(id);
        updatedLabel = label;
      }
      const sorted = sortList(options, clonedSelectedIDs, disableSort, extractStringToSort);
      hasChangeOccurred.current = true;
      unstable_batchedUpdates(() => {
        setSortedOptions(sorted);
        setSelectedIDs(clonedSelectedIDs);
        setSelectedLabel(updatedLabel);
      });
      if (isSelectionRequired) {
        if (clonedSelectedIDs.size === 0) {
          setErrorMessage('You must select an option to continue.');
          return;
        } else {
          handleOnClose(true);
        }
      }
    } else if (
      !selectedLimit ||
      clonedSelectedIDs.has(id) ||
      (!clonedSelectedIDs.has(id) && selectedLimit && selectedCount < selectedLimit)
    ) {
      if (clonedSelectedIDs.has(id)) {
        clonedSelectedIDs.delete(id);
      } else {
        clonedSelectedIDs.add(id);
      }
      const sorted = sortList(options, clonedSelectedIDs, disableSort, extractStringToSort);
      hasChangeOccurred.current = true;
      if (clonedSelectedIDs.size === 1) {
        updatedLabel = getSingleLabel(Array.from(clonedSelectedIDs)[0], options, singularLabel);
      }
      unstable_batchedUpdates(() => {
        setSortedOptions(sorted);
        setSelectedIDs(clonedSelectedIDs);
        setSelectedLabel(updatedLabel);
      });
    }
  };

  const handleSelectAll = () => {
    const clonedSelectedIDs = cloneDeep(selectedIDs);
    filteredOptions.forEach(({ id }) => {
      clonedSelectedIDs.add(id);
    });
    const sorted = sortList(options, clonedSelectedIDs, disableSort, extractStringToSort);
    hasChangeOccurred.current = true;
    let updatedLabel = '';
    if (clonedSelectedIDs.size === 1) {
      updatedLabel = getSingleLabel(Array.from(clonedSelectedIDs)[0], options, singularLabel);
    }
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSelectedIDs(clonedSelectedIDs);
      setSelectedLabel(updatedLabel);
    });
  };

  const handleClear = () => {
    const clearedSelectedIDs = new Set([]);
    const sorted = sortList(options, clearedSelectedIDs, disableSort, extractStringToSort);
    hasChangeOccurred.current = true;
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSelectedIDs(clearedSelectedIDs);
      setSelectedLabel('');
    });
  };

  const handleSelectPreset = (presetIDs: Set<string | number>) => {
    const sorted = sortList(options, presetIDs, disableSort, extractStringToSort);
    hasChangeOccurred.current = true;
    unstable_batchedUpdates(() => {
      setSortedOptions(sorted);
      setSelectedIDs(presetIDs);
      setSelectedLabel(`${presetIDs.size} ${listLabel} Selected`);
    });
  };

  const handleChangeActiveFilter = (status: EActiveFilter) => {
    setSelectedActiveStatus(status);
  };

  const listCount = options.length;
  const filteredlistCount = filteredOptions.length;
  const selectedCount = selectedIDs.size;
  const selectedMax = selectedLimit && selectedLimit < listCount ? selectedLimit : listCount;
  const isPopulated = selectedIDs.size > 0 && hasSaved;
  const pillIconColor = isPopulated
    ? publicaColors.jungleGreen
    : isDisabled
    ? publicaColors.lightGray
    : publicaColors.veryDarkGray;

  return (
    <>
      <Cols
        {...(theme === 'pill' && { onHover: () => setIsHovered(true) })}
        {...(theme === 'pill' && { onLeave: () => setIsHovered(false) })}
        extraSmallVerticalPadding
        horizontalPadding
        onClick={handleOnOpen}
        style={{
          alignItems: 'center',
          maxWidth: `${maxWidth}px`,
          minWidth: `${minWidth}px`,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          justifyContent: 'space-between',
          borderRadius: borderRadius.extraLarge,
          height: typeof height === 'number' ? `${height}px` : height,
          ...getStyle(theme, isPopulated, isHovered, isDisabled),
        }}
        gap={spacing.extraSmall}
      >
        {theme === 'pill' && iconName && (
          <Col displayFlex>
            <IconV2 name={iconName} color={pillIconColor} small />
          </Col>
        )}
        <Col>
          <Text
            small
            color={isDisabled ? publicaColors.lightGray : 'inherit'}
            style={{
              maxWidth: `${maxWidth - 48}px`,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: theme === 'pill' ? fontSizing.extraSmall : fontSizing.small,
              ...getTextStyle(theme),
            }}
          >
            {generateLabel(
              listLabel,
              singularLabel,
              placeholder,
              isSingleSelect,
              selectedIDs.size,
              listCount,
              selectedLabel,
              theme,
            )}
          </Text>
        </Col>

        {theme === 'default' && (
          <Col displayFlex style={{ marginLeft: spacing.base }}>
            <CaretMiniDownIcon color={publicaColors.gray} />
          </Col>
        )}
      </Cols>
      <Popover
        id={Boolean(anchorEl) ? 'IndependentSelect-popover' : undefined}
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        // @ts-ignore // MUI WON'T ALLOW AN EXTRA PROP
        onClose={handleOnClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            overflow: 'hidden',
            width: popoverWidth || '480px',
            border: `1px solid ${publicaColors.lightGray}`,
            boxShadow: 'none',
          },
        }}
      >
        <Rows>
          {/* STICKY SECTION */}
          <Row
            noPadding
            style={{
              borderBottom: `1px solid ${publicaColors.lightGray}`,
            }}
          >
            <Rows>
              {!disableSearch && (
                <Row smallPadding>
                  <TextInput
                    enableClear
                    fullWidth
                    handleOnChange={(value: string) => setSearch(value.toLowerCase())}
                    inputSize='small'
                    isAutoFocused
                    msDelay={500}
                    placeholder={`Search`}
                    prefix='search'
                    inputID={listLabel}
                  />
                </Row>
              )}
              <Row style={{ padding: disableSearch ? '8px 8px 6px' : '0px 8px 6px' }}>
                <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                  <Col flex>
                    <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                      {(!selectedLimit ||
                        (selectedLimit && filteredlistCount <= selectedLimit)) && (
                        <Col>
                          <TextButton
                            size='small'
                            onClick={selectedCount < totalCount ? handleSelectAll : handleClear}
                          >
                            Select All
                          </TextButton>
                        </Col>
                      )}
                      <Col>
                        <Text muted small disableSelect>
                          {`${selectedCount} /${selectedMax} Selected`}
                        </Text>
                      </Col>
                      {enableActiveFilter && selectedActiveStatus > 0 && (
                        <Col>
                          <Text highlighted extraSmall disableSelect>
                            {statuses.find((status) => status.value === selectedActiveStatus)
                              ?.message || ''}
                          </Text>
                        </Col>
                      )}
                    </Cols>
                  </Col>
                  <Cols
                    gap={spacing.base}
                    style={{ justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    {!isSingleSelectionRequired && selectedCount > 0 && (
                      <Col style={{ paddingTop: '1px' }}>
                        <TextButton size='small' onClick={handleClear}>
                          Clear
                        </TextButton>
                      </Col>
                    )}
                    {enableActiveFilter && (
                      <Col displayFlex>
                        <ActiveQuickFilter
                          selectedStatus={selectedActiveStatus}
                          handleChange={handleChangeActiveFilter}
                        />
                      </Col>
                    )}
                  </Cols>
                </Cols>
              </Row>
              {presets && (
                <Row style={{ padding: '0px 8px 6px' }}>
                  <Cols gap={spacing.base} style={{ alignItems: 'center' }}>
                    {presets.map((preset) => (
                      <Col>
                        <TextButton size='small' onClick={() => handleSelectPreset(preset.subset)}>
                          {preset.label}
                        </TextButton>
                      </Col>
                    ))}
                  </Cols>
                </Row>
              )}
            </Rows>
          </Row>

          {/* LIST SECTION */}
          {filteredlistCount ? (
            <Row noPadding>
              <FixedSizeList
                height={getHeight(filteredlistCount)}
                itemCount={filteredlistCount}
                itemSize={30}
                width={480}
              >
                {({ index, style }) =>
                  VirtualSelectRow({
                    index,
                    style,
                    displayID,
                    handleOnChange,
                    options: filteredOptions,
                    selectedIDs,
                    enableLabelTooltip,
                    isCheckboxHidden,
                  })
                }
              </FixedSizeList>
            </Row>
          ) : (
            <Row style={{ textAlign: 'center' }}>
              <Text small>No results found.</Text>
            </Row>
          )}
          {errorMessage && (
            <Row
              noPadding
              style={{
                borderTop: `1px solid ${publicaColors.lightGray}`,
              }}
            >
              <Text small color={publicaColors.red} style={{ padding: spacing.small }}>
                {errorMessage}
              </Text>
            </Row>
          )}
        </Rows>
      </Popover>
    </>
  );
};
